import React, { useId, useLayoutEffect, useRef, useState } from 'react'
import { Broom, FileText, SlidersHorizontal, WarningCircle, X } from '@phosphor-icons/react'
import FilterUbiquaMui from './FilterUbiquaMui/FilterUbiquaMui'
import UbiquaTowerBotonAction from '../UbiquaTowerBotonAction/UbiquaTowerBotonAction'
import LoadingSpinner from '../LoadingSpinner'

/** Configuración de reportes. La pantalla conserva los datos y las reglas de negocio. */
export default function ReportFilters({
  title = 'Descargar reportes por:',
  filterKey,
  filters = [],
  selections = {},
  hasSelections = false,
  hasConfiguration = false,
  disabled = false,
  canGenerate = false,
  generating = false,
  reportTitle = '',
  requirement,
  dateControls,
  onReportTitleChange,
  onFilterChange,
  onApplyFilters,
  autoApply = false,
  onReset,
  onGenerate
}) {
  const headingId = useId()
  const controlsRef = useRef(null)
  const [controlsHeight, setControlsHeight] = useState(null)
  useLayoutEffect(() => {
    const controls = controlsRef.current
    if (!controls || typeof ResizeObserver === 'undefined') return
    const updateHeight = () => setControlsHeight(controls.getBoundingClientRect().height)
    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(controls)
    return () => observer.disconnect()
  }, [])
  const controlsDisabled = disabled || generating
  return (
    <section className="report-filters" aria-labelledby={headingId}>
      <div className="report-filters__heading">
        <h3 id={headingId}>
          <SlidersHorizontal size={18} aria-hidden="true" /> {title}
        </h3>
        <span className="report-filters__required-note">
          <WarningCircle size={15} aria-hidden="true" /> Los campos con <strong>*</strong> son
          obligatorios
        </span>
      </div>
      <div
        className="report-filters__controls-viewport"
        style={{ height: controlsHeight ?? 'auto' }}
      >
        <div ref={controlsRef} className="app-list-filters report-filters__controls">
          <FilterUbiquaMui
            key={filterKey}
            title={null}
            showMoreFilters={false}
            showResetFilters={false}
            showFilterTooltips
            showSearch={false}
            showExport={false}
            filters={filters}
            appliedFilterSelections={selections}
            isAnyFilterSelected={hasSelections}
            disabled={controlsDisabled}
            onFilterChange={onFilterChange}
            onApplyFilters={onApplyFilters}
            autoApply={autoApply}
            onResetFilters={onReset}
            controlsAfterFirstFilterContent={dateControls}
            controlsBeforeMoreContent={
              hasConfiguration && (
                <button
                  type="button"
                  className="report-filters__reset"
                  onClick={onReset}
                  disabled={controlsDisabled}
                >
                  <Broom size={15} aria-hidden="true" /> Restablecer filtros
                </button>
              )
            }
          />
        </div>
      </div>
      {requirement && (
        <p className="report-filters__requirement" role="status">
          {requirement}
        </p>
      )}
      <div className="report-filters__submit-row">
        <div className="filter-ubiqua-mui-picker-search report-filters__title-control">
          <FileText size={16} aria-hidden="true" />
          <input
            aria-label="Título del reporte (opcional)"
            placeholder="Título del reporte (opcional)"
            value={reportTitle}
            disabled={controlsDisabled}
            onChange={(event) => onReportTitleChange?.(event.target.value)}
          />
          {reportTitle.length > 0 && (
            <button
              type="button"
              className="report-filters__clear-title"
              aria-label="Limpiar título del reporte"
              disabled={controlsDisabled}
              onClick={() => onReportTitleChange?.('')}
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>
        <UbiquaTowerBotonAction
          color="var(--color-action-primary)"
          textColor="var(--color-action-primary-contrast)"
          hoverColor="var(--color-action-primary-hover)"
          focusColor="var(--color-action-primary-focus-ring)"
          className={
            generating ? 'report-filters__generate is-generating' : 'report-filters__generate'
          }
          icon={
            generating ? (
              <span className="report-filters__generate-spinner" aria-hidden="true">
                <LoadingSpinner text="" size={16} />
              </span>
            ) : (
              <FileText size={17} aria-hidden="true" />
            )
          }
          label={generating ? 'Generando…' : 'Generar reporte'}
          disabled={controlsDisabled || !canGenerate}
          onClick={onGenerate}
        />
      </div>
    </section>
  )
}
